import { type GenericPolyElement, type PointBezierHandle } from '..';
import { useEditorRuntimeStore } from '../../../stores/useEditorRuntimeStore';
import { type BoundsWithCenter } from '../../../utils/editor/bounds';
import { type Point } from '../../../utils/math';

import { initHandlesFromPoints } from './bezierHandles';

export const getPolyBounds = (
  points: GenericPolyElement['points'],
  x: number,
  y: number,
): BoundsWithCenter => {
  const allX = points.map((point) => point[0]);
  const allY = points.map((point) => point[1]);

  const dx1 = Math.min(...allX) + x;
  const dy1 = Math.min(...allY) + y;
  const dx2 = Math.max(...allX) + x;
  const dy2 = Math.max(...allY) + y;
  return [dx1, dy1, dx2, dy2, (dx1 + dx2) / 2, (dy1 + dy2) / 2];
};

export const getRelativePolyBounds = (
  points: GenericPolyElement['points'],
): BoundsWithCenter => {
  return getPolyBounds(points, 0, 0);
};

export const getBoundsWithBezier = (
  points: Point[],
  bezierHandles: PointBezierHandle[] = [],
  x = 0,
  y = 0,
): BoundsWithCenter => {
  const handles =
    bezierHandles != null && bezierHandles.length === points.length
      ? bezierHandles
      : initHandlesFromPoints(points);

  const solveQuadratic = (a: number, b: number, c: number): number[] => {
    if (Math.abs(a) < 1e-10) {
      if (Math.abs(b) < 1e-10) return [];
      const t = -c / b;
      return t > 0 && t < 1 ? [t] : [];
    }

    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return [];
    const sqrtD = Math.sqrt(discriminant);
    return [(-b + sqrtD) / (2 * a), (-b - sqrtD) / (2 * a)].filter(
      (t) => t > 0 && t < 1,
    );
  };

  const bezier = (
    t: number,
    p0: number,
    p1: number,
    p2: number,
    p3: number,
  ): number => {
    const mt = 1 - t;
    return (
      mt ** 3 * p0 + 3 * mt ** 2 * t * p1 + 3 * mt * t ** 2 * p2 + t ** 3 * p3
    );
  };

  const findMinMax = (p0: number, p1: number, p2: number, p3: number) => {
    const extremaT = solveQuadratic(
      3 * (-p0 + 3 * p1 - 3 * p2 + p3),
      6 * (p0 - 2 * p1 + p2),
      3 * (p1 - p0),
    );

    const values = [p0, p3, ...extremaT.map((t) => bezier(t, p0, p1, p2, p3))];
    return { min: Math.min(...values), max: Math.max(...values) };
  };

  const bbox = {
    minX: Infinity,
    maxX: -Infinity,
    minY: Infinity,
    maxY: -Infinity,
  };

  points.forEach((p0, i) => {
    const p1 = handles[i]?.after ?? p0;
    const p2 =
      handles[(i + 1) % points.length]?.before ??
      points[(i + 1) % points.length];
    const p3 = points[(i + 1) % points.length];

    const { x, y } = {
      x: findMinMax(p0[0], p1[0], p2[0], p3[0]),
      y: findMinMax(p0[1], p1[1], p2[1], p3[1]),
    };

    bbox.minX = Math.min(bbox.minX, x.min);
    bbox.maxX = Math.max(bbox.maxX, x.max);
    bbox.minY = Math.min(bbox.minY, y.min);
    bbox.maxY = Math.max(bbox.maxY, y.max);
  });

  bbox.minX += x;
  bbox.maxX += x;
  bbox.minY += y;
  bbox.maxY += y;

  const center: Point = [
    (bbox.minX + bbox.maxX) / 2,
    (bbox.minY + bbox.maxY) / 2,
  ];

  return [bbox.minX, bbox.minY, bbox.maxX, bbox.maxY, center[0], center[1]];
};

export const getPotentialIndexOfPoint = (
  points: Point[],
  point: Point,
  bezierHandles?: PointBezierHandle[],
  threshold = 5,
) => {
  const handles = bezierHandles ?? initHandlesFromPoints(points);
  const context = useEditorRuntimeStore
    .getState()
    .staticCanvas?.getContext('2d');

  if (!context) return -1;

  const { zoom } = useEditorRuntimeStore.getState();

  context.lineWidth = threshold / zoom.value;

  const scaledPoint: Point = [point[0] * zoom.value, point[1] * zoom.value];

  for (let i = 0; i < points.length; i++) {
    const currentPoint = points[i];
    const nextPoint = points[(i + 1) % points.length];

    const path = new Path2D();
    path.moveTo(currentPoint[0], currentPoint[1]);
    path.bezierCurveTo(
      handles[i].after[0],
      handles[i].after[1],
      handles[(i + 1) % points.length].before[0],
      handles[(i + 1) % points.length].before[1],
      nextPoint[0],
      nextPoint[1],
    );
    if (context.isPointInStroke(path, scaledPoint[0], scaledPoint[1])) {
      return i + 1;
    }
  }
  return -1;
};
